/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  FormErrorMessage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Button,
  ModalFooter,
  useToast,
  Select,
  Center,
  Stack,
  Checkbox,
  Switch,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import Schemas from '../api/schemas';
import { ArtPreview } from './create.queue.component';
import { firebaseFacade } from '../api/firebase';

export default function QueueUpdateModal({ queue, isOpen, onClose, onUpdateListener }) {
  const queueNameRef = useRef();
  const queueTypeRef = useRef();
  const queueArtRef = useRef();

  const toast = useToast();
  const [errorMessage, setErrorMessage] = useState();
  const subscriptionScheduleRef = useRef();

  const { db_node_schema, queue_schema, subscription_schema } = Schemas;
  const [loading, setLoading] = useState(false);
  const [art, setArt] = useState();

  const [artUri, setArtUri] = useState(queue[queue_schema.art_uri]);
  const [name, setName] = useState(queue[queue_schema.name]);
  const [price, setPrice] = useState();

  const [priceError, setPriceError] = useState(false);
  const [subscribable, setSubscribable] = useState(false);
  const [subscription, setSubscription] = useState({});

  const [locked, setLocked] = useState(true);

  const handleQueueNameChange = ({ target: { value: name } }) => {
    if (name.length > 26) {
      setErrorMessage(
        "Please enter a name for your Queue that's 26 characters or less"
      );
      return;
    }

    setName(name);
    setErrorMessage(null);
  };

  const onUpdate = async event => {
    if (errorMessage) {
      return;
    }
    setLoading(true);
    event.preventDefault();

    const {
      current: { value: type },
    } = queueTypeRef;

    const queueCopy = {};
    const queuePath = `${db_node_schema.queues}/${
      queue[queue_schema.queue_address]
    }`;

    queueCopy[queue_schema.name] = name;
    queueCopy[queue_schema.type] = Number.parseInt(type);
    queueCopy[queue_schema.locked] = locked;

    if (subscribable) {
      const {
        current: { value: schedule },
      } = subscriptionScheduleRef;

      if (price > 0) {
        subscription[subscription_schema.price] = price;
      }

      subscription[subscription_schema.schedule] = Number.parseInt(schedule);
      queueCopy[queue_schema.subscription] = subscription;
    } else {
      queueCopy[queue_schema.subscription] = null;
    }

    try {
      queueCopy[queue_schema.art_uri] =
        (art && (await firebaseFacade.uploadArt(queue[queue_schema.queue_address], art))) ||
        queue.art_uri;
      await firebaseFacade.updateDocument(queuePath, queueCopy);
      toast({
        title: queue.name,
        description: 'Hooray! Your queue was successfully updated!',
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'top',
      });
      onUpdateListener({...queue, ...queueCopy})
      
    } catch (error) {
      toast({
        title: queue.name,
        description:
          'OhOh! We were unable to update your Queue. Please try again!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }

    setLoading(false);
    onClose();
  };

  const handleRemove = () => {
    URL.revokeObjectURL(artUri);
    setArt(null);
    setArtUri(null);

    queueArtRef.current.value = null;
    queue[
      queue_schema.art_uri
    ] = `${process.env.PUBLIC_URL}/logo-512x512-bg.png`;
  };

  const handleArtSelect = ({
    target: {
      files: [art],
    },
  }) => {
    art && handleRemove();
    setArtUri(URL.createObjectURL(art));
    setArt(art);
  };

  const handleArtSelectClick = () => {
    queueArtRef.current.click();
  };

  const handlePriceChange = ({ target: { value } }) => {
    const price = Number.parseFloat(value);
    setPrice(price);
    setPriceError(price !== 0 && price < 1e-8);
  };

  const handleAddSubs = ({ target: { checked } }) => {
    setSubscribable(checked);
  };

  const handleLock = ({ target: { checked } }) => {
    setLocked(checked);
  };

  useEffect(() => {
    const { subscription } = queue;
    setSubscription(subscription || {});
    setSubscribable(subscription !== null && subscription !== undefined);
    setPrice((subscription && subscription[subscription_schema.price]) || 0);
    setLocked(queue[queue_schema.locked]);
  }, []);

  return (
    <Modal initialFocusRef={queueNameRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Update Queue</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Stack spacing={5}>
            <FormControl isInvalid={errorMessage}>
              <FormLabel>Name</FormLabel>
              <FormErrorMessage>{errorMessage}</FormErrorMessage>
              <Input
                ref={queueNameRef}
                onChange={handleQueueNameChange}
                placeholder="Enter Queue Name"
                defaultValue={queue[queue_schema.name]}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Type</FormLabel>
              <Select
                ref={queueTypeRef}
                placeholder="Select type"
                defaultValue={Number.parseInt(queue[queue_schema.type])}
              >
                <option value={0}>Public</option>
                <option value={1}>Private</option>
              </Select>
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="locked" mb="0">
                {locked ? 'Unlock' : 'Lock'}
              </FormLabel>
              <Switch
                isChecked={locked}
                id="locked"
                colorScheme={'yellow'}
                onChange={handleLock}
              />
            </FormControl>
            <Checkbox isChecked={subscribable} onChange={handleAddSubs}>
              {subscribable ? 'Remove' : 'Add'} Subscription
            </Checkbox>
            {subscribable && (
              <>
                <FormControl isInvalid={priceError}>
                  <FormLabel>Subscription Price (QDOT)</FormLabel>
                  <Input
                    type={'number'}
                    onChange={handlePriceChange}
                    defaultValue={subscription[subscription_schema.price]}
                  />
                  {priceError && (
                    <FormErrorMessage>
                      {'Price must be 0 or >= 1E-8'}
                    </FormErrorMessage>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel>Subscription Schedule</FormLabel>
                  <Select
                    ref={subscriptionScheduleRef}
                    placeholder="Select Schedule"
                    defaultValue={subscription[subscription_schema.schedule]}
                  >
                    <option value={0}>Monthly</option>
                    <option value={1}>Yearly</option>
                  </Select>
                </FormControl>
              </>
            )}
            <FormControl>
              <Center>
                <Input
                  ref={queueArtRef}
                  type={'file'}
                  accept="image/*"
                  display={'none'}
                  onChange={handleArtSelect}
                />
                <Button onClick={handleArtSelectClick}>Upload Queue Art</Button>
              </Center>
            </FormControl>
            {artUri && <ArtPreview url={artUri} onDelete={handleRemove} />}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            onClick={onUpdate}
            bg={'transparent'}
            mr={3}
          >
            Update
          </Button>
          <Button bg={'transparent'} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
